/**
 * JobChatStream — the chat-substrate body of the Job Detail page.
 *
 * Renders Stream Model 2 (see docs/product/job-detail.md §7.1):
 *   - User messages → bubbles with sender attribution + avatar
 *   - Assistant text messages → bubbles attributed to the assigned Member
 *   - Tool calls (tool_use) → small collapsed strip beneath the bubble
 *   - emit_artifact → featured inline as a deliverable card
 *   - tool_result → hidden (they're plumbing)
 *
 * Also renders the per-user "N new turns since you were last here" separator
 * driven by job_chat_last_seen.
 */
import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Avatar } from '@/components/Avatar/Avatar';
import { t } from '@/lib/i18n';
import { formatDateTime } from '@/lib/format';
import type { JobMessage, JobMessageBlock } from '../schemas';
import styles from './JobChatStream.module.css';

export interface ChatViewer {
  /** The current viewer's tenant_user id. Used to render "You" instead of name. */
  tenantUserId: string;
}

export interface ChatMember {
  /** The assigned Member's display name (e.g. "Aria"). */
  displayName: string;
}

/** Map of tenant_user_id → display name, for messages by other users. */
export type UserAttribution = Record<string, string>;

export interface JobChatStreamProps {
  messages: JobMessage[];
  viewer: ChatViewer;
  member: ChatMember;
  /** Display-name lookup for user-role message attribution. */
  userNames: UserAttribution;
  /** Per-viewer last-seen turn number; turns greater than this get the unread separator. */
  lastSeenTurnNumber?: number | null;
  /** When true, append a "Member is working…" typing indicator after the last message. */
  isProcessing?: boolean;
}

export function JobChatStream(props: JobChatStreamProps) {
  const {
    messages,
    viewer,
    member,
    userNames,
    lastSeenTurnNumber = 0,
    isProcessing = false,
  } = props;

  // Compute first unread turn for the separator. Only show when there's a
  // real prior visit (lastSeen > 0) AND there are unread turns.
  const firstUnreadTurn = useMemo(() => {
    if (!lastSeenTurnNumber || lastSeenTurnNumber <= 0) return null;
    const unread = messages.find((m) => m.turn_number > lastSeenTurnNumber);
    return unread?.turn_number ?? null;
  }, [messages, lastSeenTurnNumber]);

  if (messages.length === 0) {
    return (
      <div className={styles.empty} data-testid="job-chat-empty">
        {t('No messages yet. The conversation will appear here as the work happens.')}
      </div>
    );
  }

  return (
    <div className={styles.stream} aria-live="polite">
      {messages.map((m) => {
        const showSeparator = firstUnreadTurn !== null && m.turn_number === firstUnreadTurn;
        return (
          <div key={m.id}>
            {showSeparator ? (
              <div className={styles.unreadSeparator} role="separator">
                <span>{t('New since you were last here')}</span>
              </div>
            ) : null}
            <MessageRow message={m} viewer={viewer} member={member} userNames={userNames} />
          </div>
        );
      })}
      {isProcessing ? <ProcessingIndicator memberName={member.displayName} /> : null}
    </div>
  );
}

function ProcessingIndicator({ memberName }: { memberName: string }) {
  return (
    <div className={styles.rowAssistant} data-testid="processing-indicator">
      <div className={styles.avatarColumn}>
        <Avatar name={memberName} size="sm" kind="ai" />
      </div>
      <div className={styles.bubbleColumnAssistant}>
        <div className={styles.attributionAssistant}>
          <span className={styles.senderName}>{memberName}</span>
        </div>
        <div className={styles.processingBubble}>
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.processingLabel}>
            {t('{name} is working…', { name: memberName })}
          </span>
        </div>
      </div>
    </div>
  );
}

function MessageRow({
  message,
  viewer,
  member,
  userNames,
}: {
  message: JobMessage;
  viewer: ChatViewer;
  member: ChatMember;
  userNames: UserAttribution;
}) {
  const time = formatDateTime(message.created_at, { hour: 'numeric', minute: '2-digit' });

  if (message.role === 'user') {
    return <UserMessageRow message={message} viewer={viewer} userNames={userNames} time={time} />;
  }
  return <AssistantMessageRow message={message} member={member} time={time} />;
}

function UserMessageRow({
  message,
  viewer,
  userNames,
  time,
}: {
  message: JobMessage;
  viewer: ChatViewer;
  userNames: UserAttribution;
  time: string;
}) {
  // user-role messages can be one of:
  //   - a kick-off / chat / awaiting-input answer (text blocks)
  //   - synthetic tool_result turns (suppressed from the stream — they're plumbing)
  const textBlocks = message.content.filter(
    (b): b is Extract<JobMessageBlock, { type: 'text' }> => b.type === 'text',
  );
  const isOnlyToolResults =
    textBlocks.length === 0 && message.content.every((b) => b.type === 'tool_result');
  if (isOnlyToolResults) return null;
  if (textBlocks.length === 0) return null;

  const senderId = message.created_by_tenant_user_id;
  const isViewer = senderId === viewer.tenantUserId;
  const senderName = isViewer
    ? t('You')
    : ((senderId ? userNames[senderId] : null) ?? t('Someone'));

  return (
    <div className={styles.rowUser} data-role="user">
      <div className={styles.bubbleColumnUser}>
        <div className={styles.attributionUser}>
          <span className={styles.senderName}>{senderName}</span>
          <span className={styles.time}>{time}</span>
        </div>
        <div className={styles.bubbleUser}>
          {textBlocks.map((b, i) => (
            <MarkdownContent key={i} text={b.text} />
          ))}
        </div>
      </div>
      <div className={styles.avatarColumn}>
        <Avatar name={senderName} size="sm" kind="human" />
      </div>
    </div>
  );
}

function AssistantMessageRow({
  message,
  member,
  time,
}: {
  message: JobMessage;
  member: ChatMember;
  time: string;
}) {
  const textBlocks = message.content.filter(
    (b): b is Extract<JobMessageBlock, { type: 'text' }> => b.type === 'text',
  );
  const toolBlocks = message.content.filter(
    (b): b is Extract<JobMessageBlock, { type: 'tool_use' }> => b.type === 'tool_use',
  );
  if (textBlocks.length === 0 && toolBlocks.length === 0) return null;

  return (
    <div className={styles.rowAssistant} data-role="assistant">
      <div className={styles.avatarColumn}>
        <Avatar name={member.displayName} size="sm" kind="ai" />
      </div>
      <div className={styles.bubbleColumnAssistant}>
        <div className={styles.attributionAssistant}>
          <span className={styles.senderName}>{member.displayName}</span>
          <span className={styles.time}>{time}</span>
        </div>
        {textBlocks.length > 0 ? (
          <div className={styles.bubbleAssistant}>
            {textBlocks.map((b, i) => (
              <MarkdownContent key={i} text={b.text} />
            ))}
          </div>
        ) : null}
        {toolBlocks.length > 0 ? (
          <div className={styles.toolStrip}>
            {toolBlocks.map((b) => (
              <ToolCallChip key={b.id} block={b} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ToolCallChip({ block }: { block: Extract<JobMessageBlock, { type: 'tool_use' }> }) {
  // emit_artifact gets a distinct visual treatment because it's user-facing
  // (the deliverable is part of the conversation).
  if (block.name === 'emit_artifact') {
    const input = (block.input ?? {}) as Record<string, unknown>;
    const param = typeof input.parameter_name === 'string' ? input.parameter_name : t('output');
    const mime = typeof input.mime_type === 'string' ? input.mime_type : '';
    return (
      <div className={styles.deliverableChip} data-testid="deliverable-chip">
        <span className={styles.deliverableIcon} aria-hidden="true">
          📄
        </span>
        <span className={styles.deliverableLabel}>{t('Emitted: {name}', { name: param })}</span>
        {mime ? <span className={styles.deliverableMime}>{mime}</span> : null}
      </div>
    );
  }

  const summary = summarizeToolCall(block);
  return (
    <div className={styles.toolChip}>
      <span className={styles.toolIcon} aria-hidden="true">
        {iconForTool(block.name)}
      </span>
      <span>{summary}</span>
    </div>
  );
}

/**
 * Markdown-rendered chat bubble content. Mirrors the markdown pipeline in
 * ArtifactView (marked → DOMPurify) so user/assistant text blocks can use
 * headings, lists, code spans, bold/italic, blockquotes, tables, etc.
 */
function MarkdownContent({ text }: { text: string }) {
  const html = useMemo(() => {
    const raw = marked.parse(text, { async: false });
    return DOMPurify.sanitize(raw);
  }, [text]);
  return <div className={styles.markdownBody} dangerouslySetInnerHTML={{ __html: html }} />;
}

function iconForTool(name: string): string {
  switch (name) {
    case 'attachment_search':
    case 'vector_search':
      return '🔍';
    case 'fetch_attachment':
      return '📎';
    case 'request_input':
      return '⏸';
    default:
      return '🔧';
  }
}

function summarizeToolCall(block: Extract<JobMessageBlock, { type: 'tool_use' }>): string {
  const input = (block.input ?? {}) as Record<string, unknown>;
  switch (block.name) {
    case 'attachment_search':
    case 'vector_search': {
      const q = typeof input.query === 'string' ? input.query : '';
      return q ? t('Searched "{q}"', { q: trim(q, 60) }) : t('Searched');
    }
    case 'fetch_attachment':
      return t('Fetched attachment');
    case 'request_input':
      return t('Asked for input');
    default:
      return t('Used {name}', { name: block.name });
  }
}

function trim(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
